import {
  buffers, type Channel,
} from 'redux-saga';
import {
  actionChannel,
  all,
  call, put, take,
  takeEvery,
} from 'redux-saga/effects';
import { SelectedDataType } from '~/_shared/components/customizeMetrics/selectedDataMetric.type';
import { CONFIG } from '~/_shared/constants/config';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { notEmpty } from '~/_shared/utils/array/array.helpers';
import type { MetricModel } from '~/_shared/utils/metric/metrics.factory';
import { select } from '~/_shared/utils/saga/effects';
import { notNullOrUndefined } from '~/_shared/utils/typeGuards';
import { createDataLayerDemographicBTGRequestParams } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/dataLayersTool.helpers';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { boundaryTerritoryGroupsRemoveCompleted } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  createOfflineBoundaryTerritoryGroup, updateOfflineBoundaryTerritoryGroups,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.sagas';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { type boundaryToolBTGsSelector } from '~/store/boundaryTerritoryGroups/boundaryTool/boundaryToolBTGs.selectors';
import { dataLayersBTGsSelector } from '~/store/boundaryTerritoryGroups/dataLayersTool/dataLayersBTGs.selectors';
import { isBTGForDataLayerDemographic } from '~/store/boundaryTerritoryGroups/dataLayersTool/dataLayersToolBTGs.helpers';
import { waitForPremiumDemographicsReady } from '~/store/demographics/premiumDemographics/demographicsListPremium.sagas';
import { demographicsListPremiumAllSelector } from '~/store/demographics/premiumDemographics/demographicsListPremium.selectors';
import { mapToolChanged } from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import {
  dataLayersToolBoundaryTerritoryGroupsCreated, dataLayersToolUpdateFinished, dataLayersToolUpdateRequest,
  dataLayersToolUpdateStarted,
} from '~/store/frontendState/mapTools/dataLayers/dataLayers.actionCreators';
import {
  mapSettingsDataLayersAddDataLayer, mapSettingsDataLayersRemoveDataLayer, mapSettingsDataLayersUpdateBoundaryGroup, mapSettingsDataLayersUpdateDataLayer,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.actionCreators';
import {
  mapSettingsActiveDataLayerSelector, mapSettingsDataLayersPrimaryBoundaryGroupIdSelector,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.selectors';
import type { DataLayer } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';
import { waitForMapSettingsReady } from '~/store/mapSettings/ready/mapSettingsReady.sagas.helpers';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { mapIdSelector } from '~/store/selectors/useMapIdSelector';

export function* mapSettingsDataLayersSaga() {
  if (CONFIG.ENABLE_DATA_LAYERS) {
    yield takeEvery([
      mapSettingsDataLayersAddDataLayer,
      mapSettingsDataLayersRemoveDataLayer,
      mapSettingsDataLayersUpdateDataLayer,
      mapSettingsDataLayersUpdateBoundaryGroup,
    ], onDataLayersChanged);
    yield takeEvery(mapToolChanged, onMapToolChanged);
    yield all([
      watchForDataLayersUpdateRequests(),
    ]);
  }
}

function* onDataLayersChanged() {
  yield put(dataLayersToolUpdateRequest());
}

function* onMapToolChanged(action: ReturnType<typeof mapToolChanged>) {
  const openedTool = action.payload.newTool;

  if (openedTool === MapTool.DataLayers) {
    yield call(waitForMapSettingsReady);
    yield call(waitForPremiumDemographicsReady);
    yield put(dataLayersToolUpdateRequest());
  }
}

function* watchForDataLayersUpdateRequests() {
  const channel: Channel<Action> = yield actionChannel(dataLayersToolUpdateRequest, buffers.sliding(1));

  while (true) {
    yield take(channel);
    try {
      yield call(updateDataLayerBoundaryGroups);
    }
    catch (error) {
      console.error('Error updating Data Layer boundary groups', error);
    }
  }
}

function* updateDataLayerBoundaryGroups() {
  yield put(dataLayersToolUpdateStarted());

  const clientId: ReturnType<typeof clientIdSelector> = yield select(clientIdSelector);
  const mapId: ReturnType<typeof mapIdSelector> = yield select(mapIdSelector);
  const currentDataLayersBTGs: ReturnType<typeof boundaryToolBTGsSelector> = yield select(dataLayersBTGsSelector);
  const boundaryGroupId: BoundaryGroupId | null = yield select(mapSettingsDataLayersPrimaryBoundaryGroupIdSelector);
  const activeDataLayer: DataLayer | null = yield select(mapSettingsActiveDataLayerSelector);
  const activeDataLayerDemographics = activeDataLayer?.data.demographics;

  const remainingDemographics = activeDataLayerDemographics ? [...activeDataLayerDemographics] : [];

  const activeDataLayersBTGs = activeDataLayer
    ? currentDataLayersBTGs.filter(btg => {
      const matchIndex = remainingDemographics.findIndex(demographic =>
        isBTGForDataLayerDemographic({
          boundaryGroupId,
          dataLayerId: activeDataLayer.id,
          demographic,
          boundaryTerritoryGroup: btg,
          mapId,
          numberOfRanges: activeDataLayer.settings.numberOfRanges,
          gradientColors: activeDataLayer.settings.style.gradientColors,
        })
      );
      if (matchIndex !== -1) {
        // each demographic should only match one BTG, so we can remove it
        // from the remainingDemographics array to prevent it from matching another BTG
        remainingDemographics.splice(matchIndex, 1);
        return true;
      }
      return false;
    })
    : [];

  // the following section removes BTGs created for the Data Layers Tool that are no longer needed
  const currentDataLayersBTGsToRemove = currentDataLayersBTGs.filter(btg => !activeDataLayersBTGs.includes(btg));
  yield put(boundaryTerritoryGroupsRemoveCompleted(currentDataLayersBTGsToRemove));

  // the following section creates new BTGs for the demographics
  // of the active data layer that do not have a corresponding BTG yet
  const demographicsToLoad = activeDataLayerDemographics?.filter(demographic =>
    !activeDataLayersBTGs.some(btg => btg.settings.demographicId === demographic)
  );

  if (!mapId || !clientId || !boundaryGroupId || !activeDataLayer) {
    yield put(dataLayersToolUpdateFinished());
    return;
  }

  // the following section updates settings of BTGs which don't require re-creation (e.g. some style changes)
  const updatedBTGs = activeDataLayersBTGs.map(btg => ({
    ...btg,
    settings: {
      ...btg.settings,
      style: activeDataLayer.settings.style,
      disableMapInteraction: activeDataLayer.settings.disableMapInteraction,
    },
  }));
  if (notEmpty(updatedBTGs)) {
    yield call(
      updateOfflineBoundaryTerritoryGroups,
      updatedBTGs,
      {
        fetchBoundaryDetails: false,
        failSilently: true,
      },
      clientId,
    );
  }

  if (!demographicsToLoad || demographicsToLoad.length === 0) {
    yield put(dataLayersToolUpdateFinished());
    return;
  }

  const demographics: ReturnType<typeof demographicsListPremiumAllSelector> = yield select(demographicsListPremiumAllSelector);
  const metrics: ReadonlyArray<MetricModel> = demographics
    .filter(d => demographicsToLoad.includes(d.id))
    .map(d => ({
      type: SelectedDataType.Demographic,
      demographic: d,
    }));

  const requests = demographicsToLoad.map((demographicId) => createDataLayerDemographicBTGRequestParams({
    dataLayerId: activeDataLayer.id,
    mapId,
    boundaryGroupId,
    demographicId,
    numberOfRanges: activeDataLayer.settings.numberOfRanges,
    metrics,
    style: {
      line_width: activeDataLayer.settings.style.lineWidth,
      line_color: activeDataLayer.settings.style.lineColor,
      gradient_colors: activeDataLayer.settings.style.gradientColors,
    },
    disableMapInteraction: activeDataLayer.settings.disableMapInteraction,
  }));

  const newBoundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield call(
    createOfflineBoundaryTerritoryGroup,
    requests,
    clientId,
  );

  if (newBoundaryTerritoryGroups) {
    const finalBTGs = demographicsToLoad.map((demographicId) => {
      return newBoundaryTerritoryGroups.find(btg => btg.boundaryGroupId === boundaryGroupId && btg.settings.demographicId === demographicId);
    }).filter(notNullOrUndefined);

    yield put(dataLayersToolBoundaryTerritoryGroupsCreated(finalBTGs));
  }
}
